import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import emailjs from '@emailjs/browser';

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  position: relative;
  background: transparent;
  color: #fff;
  overflow: hidden;
  isolation: isolate;
`;

const ContentContainer = styled.div`
  position: relative;
  z-index: 50;
  padding: 120px 5% 60px;
  pointer-events: auto;
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1.5rem;
  color: white;
  text-align: center;
  font-weight: bold;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const FormContainer = styled.form`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 40px;
  max-width: 800px;
  margin: 0 auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.5);
    background: rgba(255, 255, 255, 0.15);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.5);
    background: rgba(255, 255, 255, 0.15);
  }

  option {
    background: #1a1a1a;
    color: white;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.5);
    background: rgba(255, 255, 255, 0.15);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const SubmitButton = styled.button`
  background: white;
  color: black;
  border: none;
  border-radius: 8px;
  padding: 14px 32px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  margin-top: 16px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(255, 255, 255, 0.2);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const SuccessMessage = styled.div`
  background: rgba(0, 255, 0, 0.1);
  border: 1px solid rgba(0, 255, 0, 0.2);
  color: #00ff00;
  padding: 16px;
  border-radius: 8px;
  text-align: center;
  margin-top: 24px;
`;

const ErrorMessage = styled.div`
  background: rgba(255, 0, 0, 0.1);
  border: 1px solid rgba(255, 0, 0, 0.2);
  color: #ff0000;
  padding: 16px;
  border-radius: 8px;
  text-align: center;
  margin-top: 24px;
`;

const ContactUs = () => {
  const form = useRef();
  const submitBtn = useRef();
  const [status, setStatus] = useState({
    submitting: false,
    submitted: false,
    error: null
  });

  useEffect(() => {
    emailjs.init('jdMIZFvSFHNQfLeBN');
  }, []);

  const sendEmail = (e) => {
    e.preventDefault();
    setStatus({ submitting: true, submitted: false, error: null });

    // Log form data for debugging
    const formData = new FormData(form.current);
    console.log('Form Data:', {
      name: formData.get('name'),
      company: formData.get('company'),
      title: formData.get('title'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      country: formData.get('country'),
      message: formData.get('message')
    });

    emailjs.sendForm('service_0v41bzd', 'template_n6s6wp8', form.current, 'jdMIZFvSFHNQfLeBN')
      .then(() => {
        setStatus({
          submitting: false,
          submitted: true,
          error: null
        });
        form.current.reset();
      }, (err) => {
        setStatus({
          submitting: false,
          submitted: false,
          error: 'Failed to send message. Please try again.'
        });
        console.error('Error:', err);
      });
  };

  return (
    <PageContainer>
      <Navbar />
      <ContentContainer>
        <Title>Contact Us</Title>
        <Subtitle>
          Have questions about our Earth operations platform? We'd love to hear from you.
          Fill out the form below and we'll get back to you as soon as possible.
        </Subtitle>

        <FormContainer ref={form} onSubmit={sendEmail}>
          <FormGrid>
            <FormField>
              <Label htmlFor="name">Name *</Label>
              <Input
                type="text"
                id="name"
                name="name"
                placeholder="Enter your full name"
                required
              />
            </FormField>

            <FormField>
              <Label htmlFor="company">Company Name *</Label>
              <Input
                type="text"
                id="company"
                name="company"
                placeholder="Enter your company name"
                required
              />
            </FormField>

            <FormField>
              <Label htmlFor="title">Title *</Label>
              <Input
                type="text"
                id="title"
                name="title"
                placeholder="Enter your job title"
                required
              />
            </FormField>

            <FormField>
              <Label htmlFor="email">Email *</Label>
              <Input
                type="email"
                id="email"
                name="email"
                placeholder="Enter your email address"
                required
              />
            </FormField>

            <FormField>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                type="tel"
                id="phone"
                name="phone"
                placeholder="Enter your phone number"
              />
            </FormField>

            <FormField>
              <Label htmlFor="country">Country *</Label>
              <Select
                id="country"
                name="country"
                required
              >
                <option value="">Select your country</option>
                <option value="US">United States</option>
                <option value="UK">United Kingdom</option>
                <option value="CA">Canada</option>
                <option value="AU">Australia</option>
                <option value="DE">Germany</option>
                <option value="FR">France</option>
                <option value="JP">Japan</option>
                <option value="Other">Other</option>
              </Select>
            </FormField>
          </FormGrid>

          <FormField>
            <Label htmlFor="message">Message *</Label>
            <TextArea
              id="message"
              name="message"
              placeholder="Tell us about your needs and how we can help..."
              required
            />
          </FormField>

          <SubmitButton 
            type="submit" 
            id="button"
            disabled={status.submitting}
          >
            {status.submitting ? 'Sending...' : 'Send Message'}
          </SubmitButton>

          {status.submitted && (
            <SuccessMessage>
              Thank you for your message! We'll get back to you soon.
            </SuccessMessage>
          )}

          {status.error && (
            <ErrorMessage>{status.error}</ErrorMessage>
          )}
        </FormContainer>
      </ContentContainer>
      <Footer />
    </PageContainer>
  );
};

export default ContactUs; 